import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import {
  confirmAssistantAction,
  sendAssistantChat,
} from '../services/assistantService';
import type { PendingAction } from '../services/assistantService';
import { getActiveProperties, getProperty } from '../services/propertyService';
import type { Property } from '../types/property';
import { useFeature } from '../hooks/useFeatures';
import { palette } from '../theme';

interface ChatLine {
  id: string;
  from: 'user' | 'assistant';
  text: string;
  createdAt: Date;
}

function formatAddress(p: Property): string {
  const a = p.address;
  if (!a) return p._id;
  if ((a as any).formattedAddress) return (a as any).formattedAddress;
  return [a.street, a.city, a.state].filter(Boolean).join(', ') || p._id;
}

function mergePropertyIntoList(list: Property[], property: Property): Property[] {
  if (list.some((p) => p._id === property._id)) return list;
  return [property, ...list];
}

const PropertyAssistantChat: React.FC = () => {
  const { agencySlug, propertyId: paramPropertyId } = useParams<{
    agencySlug: string;
    propertyId?: string;
  }>();
  const location = useLocation();
  const { hasAccess, loading: featureLoading } = useFeature('property_assistant');

  const routePropertyId = useMemo(() => {
    if (paramPropertyId && /^[a-fA-F0-9]{24}$/.test(paramPropertyId)) {
      return paramPropertyId;
    }
    const match = location.pathname.match(/\/properties\/([a-fA-F0-9]{24})(?:\/|$)/);
    return match?.[1] || null;
  }, [location.pathname, paramPropertyId]);

  const [open, setOpen] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [loadingProps, setLoadingProps] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [messages, setMessages] = useState<ChatLine[]>([]);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const selectedPropertyRef = useRef<Property | null>(null);
  selectedPropertyRef.current = selectedProperty;

  const bindProperty = (property: Property, resetChat: boolean) => {
    setSelectedProperty(property);
    if (resetChat) {
      setConversationId(undefined);
      setMessages([]);
      setPendingActions([]);
    }
  };

  const loadProperties = React.useCallback(
    async (opts?: { preferPropertyId?: string | null }) => {
      if (!agencySlug) return;
      const preferId = opts?.preferPropertyId ?? routePropertyId;
      try {
        setLoadingProps(true);
        setError(null);
        const data = await getActiveProperties(agencySlug, 1, 200);
        let list = data.properties || [];

        // Ensure the current page property is in the picker (even if newly created)
        if (preferId && !list.some((p) => p._id === preferId)) {
          try {
            const current = await getProperty(agencySlug, preferId);
            if (current?._id) {
              list = mergePropertyIntoList(list, current);
            }
          } catch {
            // Property may be archived / inaccessible; keep list as-is
          }
        }

        setProperties(list);

        const preferred =
          (preferId && list.find((p) => p._id === preferId)) || null;
        const currentId = selectedPropertyRef.current?._id;

        // On a property page with nothing selected → bind that property as context
        if (!currentId && preferred) {
          bindProperty(preferred, false);
          return;
        }

        // Keep prior selection if still in the list
        if (
          currentId &&
          !list.some((p) => p._id === currentId) &&
          preferred
        ) {
          bindProperty(preferred, true);
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load properties');
      } finally {
        setLoadingProps(false);
      }
    },
    [agencySlug, routePropertyId]
  );

  // Refresh whenever the panel opens, or when navigating to another property while open
  useEffect(() => {
    if (!open || !agencySlug) return;
    void loadProperties({ preferPropertyId: routePropertyId });
  }, [open, agencySlug, routePropertyId, loadProperties]);

  // If chat is open on a property page and nothing is selected, bind the page property
  useEffect(() => {
    if (!open || !agencySlug || !routePropertyId) return;
    if (selectedProperty) return;

    let cancelled = false;
    (async () => {
      const fromList = properties.find((p) => p._id === routePropertyId);
      if (fromList) {
        if (!cancelled) setSelectedProperty(fromList);
        return;
      }
      try {
        const current = await getProperty(agencySlug, routePropertyId);
        if (cancelled || !current?._id) return;
        setProperties((prev) => mergePropertyIntoList(prev, current));
        setSelectedProperty(current);
      } catch {
        // ignore — user can pick manually
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, agencySlug, routePropertyId, selectedProperty, properties]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingActions, open]);

  if (featureLoading || !hasAccess || !agencySlug) return null;

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !selectedProperty || sending) return;
    setError(null);
    setSending(true);
    const userLine: ChatLine = {
      id: `u-${Date.now()}`,
      from: 'user',
      text,
      createdAt: new Date(),
    };
    setMessages((prev) => [...prev, userLine]);
    setInput('');
    try {
      const res = await sendAssistantChat(agencySlug, {
        propertyId: selectedProperty._id,
        message: text,
        conversationId,
      });
      setConversationId(res.conversationId);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          from: 'assistant',
          text: res.reply,
          createdAt: new Date(),
        },
      ]);
      // Backend already returns pending-only; treat missing status as pending
      setPendingActions(
        (res.pendingActions || []).filter(
          (a) => !a.status || a.status === 'pending'
        )
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Chat failed');
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = async (actionId: string, confirm: boolean) => {
    if (!conversationId) return;
    setConfirmingId(actionId);
    setError(null);
    const removed = pendingActions.find((a) => a.id === actionId);
    // Optimistically remove so the card never sticks after a successful confirm
    setPendingActions((prev) => prev.filter((a) => a.id !== actionId));
    try {
      const res = await confirmAssistantAction(agencySlug, {
        conversationId,
        actionId,
        confirm,
      });
      setMessages((prev) => [
        ...prev,
        {
          id: `sys-${Date.now()}`,
          from: 'assistant',
          text:
            res.message ||
            (confirm
              ? `Done: ${res.action.summary}`
              : `Cancelled: ${res.action.summary}`),
          createdAt: new Date(),
        },
      ]);
      if (res.action?.result?.draft) {
        const d = res.action.result.draft;
        setMessages((prev) => [
          ...prev,
          {
            id: `draft-${Date.now()}`,
            from: 'assistant',
            text: `Email draft\nTo: ${(d.to || []).join(', ') || '(none)'}\nSubject: ${d.subject}\n\n${d.body}`,
            createdAt: new Date(),
          },
        ]);
      }
    } catch (err: any) {
      if (removed) {
        setPendingActions((prev) =>
          prev.some((a) => a.id === actionId) ? prev : [...prev, removed]
        );
      }
      setError(err?.response?.data?.error || err?.message || 'Action failed');
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <>
      {!open && (
        <Tooltip
          title="Digital Assistant — ask about properties, notes, reminders, and email drafts"
          placement="left"
          arrow
        >
          <IconButton
            onClick={() => setOpen(true)}
            aria-label="Open Digital Assistant"
            sx={{
              position: 'fixed',
              right: 24,
              bottom: 24,
              zIndex: 1300,
              width: 56,
              height: 56,
              bgcolor: palette.accent,
              color: '#fff',
              boxShadow: 3,
              '&:hover': { bgcolor: palette.accentDark },
            }}
          >
            <SmartToyIcon />
          </IconButton>
        </Tooltip>
      )}

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 1300,
            width: { xs: 'calc(100vw - 32px)', sm: 400 },
            maxWidth: 420,
            height: 560,
            maxHeight: 'calc(100vh - 96px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: palette.accent,
              color: '#fff',
            }}
          >
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 32, height: 32 }}>
              <SmartToyIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                Property Assistant
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
                Agency agents · property Q&A and actions
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Autocomplete
              size="small"
              loading={loadingProps}
              options={properties}
              value={selectedProperty}
              onOpen={() => {
                // Re-fetch when opening the picker so newly added properties appear
                void loadProperties({ preferPropertyId: routePropertyId });
              }}
              onChange={(_, value) => {
                setSelectedProperty(value);
                setConversationId(undefined);
                setMessages([]);
                setPendingActions([]);
              }}
              getOptionLabel={(p) => formatAddress(p)}
              isOptionEqualToValue={(a, b) => a._id === b._id}
              renderInput={(params) => (
                <TextField {...params} label="Property" placeholder="Select a property" />
              )}
            />
            {routePropertyId && selectedProperty?._id === routePropertyId && (
              <Chip
                size="small"
                label="Bound to this page"
                sx={{ mt: 1 }}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
            {!selectedProperty && (
              <Typography variant="body2" color="text.secondary">
                Select a property to start chatting.
              </Typography>
            )}
            {selectedProperty && messages.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Ask about status, inspections, offers, notes, or request reminders and email drafts.
              </Typography>
            )}
            {messages.map((m) => (
              <Box
                key={m.id}
                sx={{
                  display: 'flex',
                  justifyContent: m.from === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: m.from === 'user' ? 'primary.main' : 'grey.100',
                    color: m.from === 'user' ? '#fff' : 'text.primary',
                    whiteSpace: 'pre-wrap',
                    fontSize: 14,
                  }}
                >
                  {m.text}
                </Box>
              </Box>
            ))}

            {pendingActions
              .filter((action) => !action.status || action.status === 'pending')
              .map((action) => (
              <Paper
                key={action.id}
                variant="outlined"
                sx={{ p: 1.25, mb: 1, bgcolor: 'warning.50' }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
                  Confirm action
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {action.summary}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={confirmingId === action.id}
                    onClick={() => handleConfirm(action.id, true)}
                  >
                    {confirmingId === action.id ? <CircularProgress size={16} /> : 'Confirm'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={confirmingId === action.id}
                    onClick={() => handleConfirm(action.id, false)}
                  >
                    Cancel
                  </Button>
                </Box>
              </Paper>
            ))}
            <div ref={endRef} />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Divider />
          <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={selectedProperty ? 'Ask about this property…' : 'Select a property first'}
              value={input}
              disabled={!selectedProperty || sending}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              multiline
              maxRows={3}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!selectedProperty || !input.trim() || sending}
            >
              {sending ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default PropertyAssistantChat;
